import { useState, useEffect, useRef } from 'react';
import {
  motion,
  useMotionValue,
  useTransform,
  useAnimation,
  AnimatePresence,
} from 'framer-motion';
import { Volume2, VolumeX, Sparkles, RotateCcw } from 'lucide-react';
import confetti from 'canvas-confetti';
import { haptic } from '@/lib/haptic';
import type { Word, SwipeResult } from '@/types';

interface SwipeCardProps {
  word: Word;
  onSwipe: (result: SwipeResult) => void;
  onRewind?: () => void;
  canRewind?: boolean;
  speak: (text: string, lang?: string) => void;
  stop: () => void;
  isAudioPlaying: boolean;
}

type SwipeHint = SwipeResult | null;

export default function SwipeCard({
  word,
  onSwipe,
  onRewind,
  canRewind = false,
  speak,
  stop,
  isAudioPlaying,
}: SwipeCardProps) {
  const [isRevealed, setIsRevealed] = useState(false);
  const [swipeHint, setSwipeHint] = useState<SwipeHint>(null);
  const [isDragging, setIsDragging] = useState(false);
  // 'word' | 'example' | null — タップ時に即座にセットしてタイミングずれを回避
  const [playingSource, setPlayingSource] = useState<'word' | 'example' | null>(null);
  const hasSwipedRef          = useRef(false);
  const wordTouchStartRef     = useRef<{ x: number; y: number } | null>(null);
  const exTouchStartRef       = useRef<{ x: number; y: number } | null>(null);
  const wordTouchHandledRef   = useRef(false);
  const exTouchHandledRef     = useRef(false);

  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotate = useTransform(x, [-220, 0, 220], [-14, 0, 14]);
  const controls = useAnimation();

  const audioMuted = () =>
    typeof window !== 'undefined' && localStorage.getItem('locomoco_mute_audio') === '1';

  // 通常アポストロフィ → IPA正式ストレスマーク（上付き縦線）に変換
  const formatPhonetic = (p: string) =>
    p.replace(/'/g, 'ˈ').replace(/,/g, 'ˌ');

  // 音声が自然終了したらソースをリセット
  useEffect(() => {
    if (!isAudioPlaying) setPlayingSource(null);
  }, [isAudioPlaying]);

  useEffect(() => {
    hasSwipedRef.current = false;
    setIsRevealed(false);
    setSwipeHint(null);
    setPlayingSource(null);
    x.set(0);
    y.set(0);
    controls.set({ x: 0, y: 0, rotate: 0, opacity: 1, scale: 1 });

    if (!audioMuted()) {
      const timer = setTimeout(() => {
        speak(word.word);
      }, 180);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [word.id]);

  const handleReveal = () => {
    if (!isDragging) { haptic(8); setIsRevealed(true); }
  };

  const triggerSwipe = async (result: SwipeResult) => {
    if (hasSwipedRef.current) return;
    hasSwipedRef.current = true;
    setSwipeHint(null);

    if (result === 'perfect') {
      haptic([15, 40, 15]); // ダブルバズ
      confetti({
        particleCount: 220,
        spread: 100,
        origin: { x: 0.5, y: 0.55 },
        colors: ['#FFD700', '#FFA500', '#FF6347', '#7B68EE', '#00CED1', '#FF69B4'],
        startVelocity: 50,
        gravity: 0.8,
      });
      await controls.start({
        y: -700,
        scale: 1.25,
        opacity: 0,
        transition: { duration: 0.38, ease: 'easeOut' },
      });
    } else if (result === 'skip') {
      haptic(20);
      await controls.start({
        x: -700,
        y: 80,
        rotate: -22,
        opacity: 0,
        transition: { duration: 0.3, ease: 'easeOut' },
      });
    } else {
      haptic(12);
      await controls.start({
        x: 700,
        y: 80,
        rotate: 22,
        opacity: 0,
        transition: { duration: 0.3, ease: 'easeOut' },
      });
    }

    onSwipe(result);
  };

  const handleDrag = (_: unknown, info: { offset: { x: number; y: number } }) => {
    setIsDragging(true);
    const { offset } = info;
    const dominantUp = offset.y < -40 && Math.abs(offset.y) > Math.abs(offset.x) * 1.3;
    const dominantLeft = offset.x < -40 && !dominantUp;
    const dominantRight = offset.x > 40 && !dominantUp;

    if (dominantUp) setSwipeHint('perfect');
    else if (dominantLeft) setSwipeHint('skip');
    else if (dominantRight) setSwipeHint('know');
    else setSwipeHint(null);
  };

  const handleDragEnd = async (
    _: unknown,
    info: { offset: { x: number; y: number } }
  ) => {
    setIsDragging(false);
    const { offset } = info;

    const isUp = offset.y < -70 && Math.abs(offset.y) > Math.abs(offset.x) * 1.2;
    const isLeft = offset.x < -70 && !isUp;
    const isRight = offset.x > 70 && !isUp;

    if (isUp) {
      await triggerSwipe('perfect');
    } else if (isLeft) {
      await triggerSwipe('skip');
    } else if (isRight) {
      await triggerSwipe('know');
    } else {
      setSwipeHint(null);
      controls.start({
        x: 0,
        y: 0,
        rotate: 0,
        transition: { type: 'spring', stiffness: 400, damping: 28 },
      });
    }
  };

  /** タップ判定：移動量が threshold px 未満なら true */
  const isTap = (
    startRef: React.MutableRefObject<{ x: number; y: number } | null>,
    e: React.TouchEvent,
    threshold = 10,
  ): boolean => {
    if (!startRef.current) return false;
    const t  = e.changedTouches[0];
    const dx = Math.abs(t.clientX - startRef.current.x);
    const dy = Math.abs(t.clientY - startRef.current.y);
    startRef.current = null;
    return dx < threshold && dy < threshold;
  };

  // ── 単語音声 ──────────────────────────────────────────────────────────
  const playWord = () => {
    if (audioMuted()) return;
    haptic(8);
    if (playingSource === 'word') {
      stop(); setPlayingSource(null);
    } else {
      setPlayingSource('word'); speak(word.word);
    }
  };
  const handleWordAudio = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (wordTouchHandledRef.current) { wordTouchHandledRef.current = false; return; }
    playWord();
  };
  const handleWordTouchStart = (e: React.TouchEvent) => {
    wordTouchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  };
  const handleWordTouchEnd = (e: React.TouchEvent) => {
    e.stopPropagation();
    if (isTap(wordTouchStartRef, e)) { wordTouchHandledRef.current = true; playWord(); }
  };

  // ── 例文音声 ──────────────────────────────────────────────────────────
  const playExample = () => {
    if (audioMuted()) return;
    haptic(8);
    if (playingSource === 'example') {
      stop(); setPlayingSource(null);
    } else {
      setPlayingSource('example'); speak(word.example_sentence);
    }
  };
  const handleExampleAudio = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (exTouchHandledRef.current) { exTouchHandledRef.current = false; return; }
    playExample();
  };
  const handleExampleTouchStart = (e: React.TouchEvent) => {
    exTouchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  };
  const handleExampleTouchEnd = (e: React.TouchEvent) => {
    e.stopPropagation();
    if (isTap(exTouchStartRef, e)) { exTouchHandledRef.current = true; playExample(); }
  };

  return (
    /* 外枠: 親の flex-1 を受け取り、縦いっぱいに広がる */
    <div className="flex flex-col w-full max-w-sm mx-auto h-full">

      {/* ドラッグ領域（flex-1 で残りの高さをすべて取る） */}
      <div className="flex-1 relative min-h-0">
        <motion.div
          drag
          dragConstraints={{ left: 0, right: 0, top: 0, bottom: 0 }}
          dragElastic={0.85}
          dragMomentum={false}
          onDrag={handleDrag}
          onDragEnd={handleDragEnd}
          animate={controls}
          style={{ x, y, rotate }}
          className="absolute inset-0 cursor-grab active:cursor-grabbing touch-none"
        >
          {/* スワイプオーバーレイ */}
          <AnimatePresence>
            {swipeHint === 'skip' && (
              <motion.div
                key="skip"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.08 }}
                className="absolute inset-0 z-20 rounded-3xl border-4 border-red-500 bg-red-500/15 pointer-events-none flex items-start justify-start p-5"
              >
                <div className="bg-red-500 rounded-xl px-4 py-1.5 -rotate-12 shadow-lg">
                  <span className="text-white font-black text-lg">また後で</span>
                </div>
              </motion.div>
            )}
            {swipeHint === 'know' && (
              <motion.div
                key="know"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.08 }}
                className="absolute inset-0 z-20 rounded-3xl border-4 border-emerald-500 bg-emerald-500/15 pointer-events-none flex items-start justify-end p-5"
              >
                <div className="bg-emerald-500 rounded-xl px-4 py-1.5 rotate-12 shadow-lg">
                  <span className="text-white font-black text-lg">知っている</span>
                </div>
              </motion.div>
            )}
            {swipeHint === 'perfect' && (
              <motion.div
                key="perfect"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.08 }}
                className="absolute inset-0 z-20 rounded-3xl border-4 border-violet-500 bg-violet-500/15 pointer-events-none flex items-end justify-center p-5"
              >
                <div className="bg-gradient-to-r from-violet-500 to-purple-600 rounded-2xl px-6 py-2 shadow-lg shadow-violet-500/40 flex items-center gap-2">
                  <Sparkles size={16} className="text-white" />
                  <span className="text-white font-black text-lg tracking-tight">もう完璧！</span>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* カード本体 */}
          <div
            className="absolute inset-0 bg-white dark:bg-slate-900 rounded-3xl shadow-xl border border-slate-100 dark:border-slate-800 overflow-hidden flex flex-col"
            onClick={!isRevealed ? handleReveal : undefined}
          >
            {!isRevealed ? (
              /* 表面 */
              <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
                <button
                  onTouchStart={handleWordTouchStart}
                  onTouchEnd={handleWordTouchEnd}
                  onClick={handleWordAudio}
                  className="relative mb-4 group active:scale-95 transition-transform"
                >
                  <h2 className="text-5xl font-black text-slate-900 dark:text-white tracking-tight leading-none [font-family:var(--font-heading)]">
                    {word.word}
                  </h2>
                  <motion.div
                    animate={playingSource === 'word' ? { scale: [1, 1.25, 1] } : {}}
                    transition={{ repeat: Infinity, duration: 0.65 }}
                    className={`absolute -top-2 -right-10 p-2 rounded-full ${
                      playingSource === 'word'
                        ? 'bg-indigo-600 text-white'
                        : 'bg-indigo-50 dark:bg-indigo-950 text-indigo-500'
                    }`}
                  >
                    {playingSource === 'word' ? <VolumeX size={16} /> : <Volume2 size={16} />}
                  </motion.div>
                </button>

                <p className="text-lg text-indigo-500 dark:text-indigo-400 tracking-widest [font-family:var(--font-phonetic)]">
                  {word.phonetic ? `/${formatPhonetic(word.phonetic)}/` : ""}
                </p>

                <div className="mt-auto pt-8 flex flex-col items-center gap-2.5">
                  <div className="flex gap-1.5">
                    {[0, 1, 2].map((i) => (
                      <motion.div
                        key={i}
                        animate={{ y: [0, -5, 0] }}
                        transition={{ repeat: Infinity, duration: 1.2, delay: i * 0.15 }}
                        className="w-2 h-2 bg-slate-200 dark:bg-slate-700 rounded-full"
                      />
                    ))}
                  </div>
                  <p className="text-sm font-semibold text-slate-400 dark:text-slate-500">
                    タップして意味を確認
                  </p>
                </div>
              </div>
            ) : (
              /* 裏面: 表面と同じ上部構造で単語位置を固定 */
              <div className="flex-1 flex flex-col items-center p-8 text-center overflow-hidden">
                {/* 単語 + 発音記号 */}
                <button
                  onTouchStart={handleWordTouchStart}
                  onTouchEnd={handleWordTouchEnd}
                  onClick={handleWordAudio}
                  className="relative mb-4 active:scale-95 transition-transform"
                >
                  <h2 className="text-5xl font-black text-slate-900 dark:text-white tracking-tight leading-none [font-family:var(--font-heading)]">
                    {word.word}
                  </h2>
                  <motion.div
                    animate={playingSource === 'word' ? { scale: [1, 1.25, 1] } : {}}
                    transition={{ repeat: Infinity, duration: 0.65 }}
                    className={`absolute -top-2 -right-10 p-2 rounded-full ${
                      playingSource === 'word'
                        ? 'bg-indigo-600 text-white'
                        : 'bg-indigo-50 dark:bg-indigo-950 text-indigo-500'
                    }`}
                  >
                    {playingSource === 'word' ? <VolumeX size={16} /> : <Volume2 size={16} />}
                  </motion.div>
                </button>

                <p className="text-lg text-indigo-500 dark:text-indigo-400 tracking-widest [font-family:var(--font-phonetic)]">
                  {word.phonetic ? `/${formatPhonetic(word.phonetic)}/` : ""}
                </p>

                {/* 意味・例文: 単語の直下に固定間隔で配置 */}
                <div className="mt-6 w-full">
                  <div className="h-px bg-slate-100 dark:bg-slate-800 mb-3" />

                  <p className="text-xl font-black text-slate-900 dark:text-white leading-snug mb-3 text-center">
                    {word.meaning}
                  </p>

                  <div className="h-px bg-slate-100 dark:bg-slate-800 mb-2" />

                  {/* 例文（タップで再生 / 再生中はタップで停止） */}
                  <button
                    onTouchStart={handleExampleTouchStart}
                    onTouchEnd={handleExampleTouchEnd}
                    onClick={handleExampleAudio}
                    className="w-full text-left"
                  >
                    <div className={`flex items-start gap-2.5 p-3.5 rounded-2xl transition-colors ${
                      playingSource === 'example'
                        ? 'bg-indigo-50 dark:bg-indigo-900/30 border border-indigo-300 dark:border-indigo-700'
                        : 'bg-slate-50 dark:bg-slate-800'
                    }`}>
                      {playingSource === 'example' ? (
                        <motion.div
                          animate={{ scale: [1, 1.2, 1] }}
                          transition={{ repeat: Infinity, duration: 0.65 }}
                        >
                          <VolumeX size={16} className="text-indigo-600 dark:text-indigo-400 mt-0.5 shrink-0" />
                        </motion.div>
                      ) : (
                        <Volume2 size={16} className="text-indigo-400 mt-0.5 shrink-0" />
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="text-base text-slate-700 dark:text-slate-300 font-medium leading-relaxed">
                          {word.example_sentence}
                        </p>
                        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1.5 leading-relaxed">
                          {word.example_translation}
                        </p>
                        {playingSource === 'example' && (
                          <p className="text-xs text-indigo-500 dark:text-indigo-400 mt-1 font-bold">
                            タップして停止
                          </p>
                        )}
                      </div>
                    </div>
                  </button>
                </div>

                {/* スワイプガイドだけ下端に固定 */}
                <div className="mt-auto pt-3 w-full flex items-center justify-between text-[11px] font-black uppercase tracking-wider px-1">
                  <span className="text-red-400">⬅ また後で</span>
                  <span className="text-violet-400">⬆ 完璧</span>
                  <span className="text-emerald-400">知っている ➡</span>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </div>

      {/* 下部ボタン: 完璧を画面中央に、リワインドは左端に独立配置 */}
      <div className="relative h-24 w-full max-w-sm mx-auto shrink-0">
        <button
          onClick={() => { if (canRewind && onRewind) { haptic(10); onRewind(); } }}
          disabled={!canRewind}
          className={`absolute left-4 top-1/2 -translate-y-1/2 w-[44px] h-[44px] rounded-xl flex items-center justify-center transition-all active:scale-90 ${
            canRewind
              ? 'bg-white dark:bg-slate-900 border-2 border-slate-300 dark:border-slate-600 text-slate-500 shadow-md'
              : 'bg-slate-100 dark:bg-slate-800 border-2 border-slate-200 dark:border-slate-700 text-slate-300 dark:text-slate-600'
          }`}
          aria-label="リワインド"
        >
          <RotateCcw size={17} />
        </button>

        <div className="h-full flex items-center justify-center gap-3">
          <button
            onClick={() => { haptic(20); triggerSwipe('skip'); }}
            className="w-[60px] h-[60px] rounded-2xl bg-white dark:bg-slate-900 border-2 border-red-400/70 text-red-400 flex flex-col items-center justify-center gap-0.5 shadow-md active:scale-90 transition-transform"
            aria-label="また後で"
          >
            <span className="text-xl font-black leading-none">✕</span>
            <span className="text-[9px] font-bold text-red-400/80 leading-none">また後で</span>
          </button>

          <button
            onClick={() => { haptic([15, 40, 15]); triggerSwipe('perfect'); }}
            className="w-[76px] h-[76px] rounded-2xl bg-gradient-to-br from-violet-500 to-purple-700 text-white flex flex-col items-center justify-center gap-1 shadow-xl shadow-violet-500/40 active:scale-90 transition-transform"
            aria-label="もう完璧！"
          >
            <Sparkles size={26} className="text-white drop-shadow" />
            <span className="text-[9px] font-black leading-none tracking-wide">完璧！</span>
          </button>

          <button
            onClick={() => { haptic(12); triggerSwipe('know'); }}
            className="w-[60px] h-[60px] rounded-2xl bg-white dark:bg-slate-900 border-2 border-emerald-400/70 text-emerald-500 flex flex-col items-center justify-center gap-0.5 shadow-md active:scale-90 transition-transform"
            aria-label="知っている"
          >
            <span className="text-xl font-black leading-none">✓</span>
            <span className="text-[9px] font-bold text-emerald-500/80 leading-none">知ってる</span>
          </button>
        </div>
      </div>
    </div>
  );
}
